import {Link} from 'react-router';
import {useAgenteDeCalidad} from '../api/calidad';
import {useResumenDeCumplimiento} from '../api/cumplimiento';
import {useEstadoDeNavieras} from '../api/navieras';
import {useAlertasDeReservas} from '../api/reservas';
import {useAlertasDeSeguimiento} from '../api/seguimiento';
import {tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {AYUDA_PENDIENTES} from './ayuda-general';
import {ve} from './areas';
import {NotaDeAyuda} from './nota-de-ayuda';
import {useUsuario} from './sesion-contexto';

// "Pendientes hoy" del inicio (menu nuevo, 6/10/2026): lo que pide atencion
// hoy, reunido de lo que la app ya calcula. No hay calculo nuevo: reservas con
// algo pendiente (20, 16 y 3 dias), alertas propias en su ventana de aviso,
// llegadas vencidas o de hoy, dias libres en puerto por acabarse, alertas de
// calidad nuevas, cambios de las navieras por revisar y documentos de
// cumplimiento vencidos o por vencer (decision 0045).
// Compacto (Daniel, 7/10/2026): una linea por grupo con fichas cortas; el
// detalle completo va en la ayuda al pasar el raton.
const MAXIMO = 3;

// resaltado: reserva que zarpa en 3 dias o menos, con su propio color (7/10/2026).
type Item = {
  clave: string;
  texto: string;
  detalle?: string;
  ayuda?: string;
  enlace: string;
  urgente?: boolean;
  resaltado?: boolean;
};
type Grupo = {clave: string; titulo: string; items: Item[]; total: number; verTodo: string};

export function PendientesHoy() {
  // Cada grupo solo para quien ve su seccion (decision 0062).
  const usuario = useUsuario();
  const veReservas = ve(usuario, 'exportaciones.reservas');
  const veSeguimiento = ve(usuario, 'exportaciones.seguimiento');
  const veCalidad = ve(usuario, 'analisis.calidad');
  const veNavieras = ve(usuario, 'exportaciones.navieras');
  const veCumplimiento = ve(usuario, 'cumplimiento');
  const reservas = useAlertasDeReservas(veReservas);
  const seguimiento = useAlertasDeSeguimiento({}, veSeguimiento);
  const calidad = useAgenteDeCalidad(veCalidad);
  const navieras = useEstadoDeNavieras(veNavieras);
  const cumplimiento = useResumenDeCumplimiento(veCumplimiento);

  const deReservas = (reservas.data?.alertas ?? []).filter(a => a.nivel !== 'lista');
  const propias = (seguimiento.data?.personalizadas ?? []).filter(a => a.estado === 'activa' && a.visible);
  const llegadas = (seguimiento.data?.arribos ?? []).filter(a => a.tono !== 'proxima');
  const puerto = (seguimiento.data?.puerto ?? []).filter(p => p.diasConCobro > 0 || p.diasLibresRestantes <= 2);
  const deCalidad = (calidad.data?.alertas ?? []).filter(a => a.nueva && a.estado !== 'resuelta');
  const cambiosDeNavieras = navieras.data?.cambiosPorRevisar ?? 0;
  const deCumplimiento = cumplimiento.data?.vencimientos ?? [];

  const grupos: Grupo[] = [
    {
      clave: 'reservas',
      titulo: tr('Reservas', 'Bookings'),
      total: deReservas.length,
      verTodo: '/seguimiento/alertas-de-reservas',
      items: deReservas.map(a => ({
        clave: a.clave,
        texto: a.reserva || a.exportacion || a.titulo,
        detalle:
          a.diasParaSalida === 0
            ? tr('zarpa hoy', 'departs today')
            : tr(`sale en ${a.diasParaSalida} días`, `departs in ${a.diasParaSalida} days`),
        ayuda: [a.titulo, a.exportacion].filter(Boolean).join(' · '),
        enlace: `/seguimiento/reservas?id=${a.reservaId}`,
        urgente: a.nivel === 'pendiente',
        resaltado: a.urgente,
      })),
    },
    {
      clave: 'propias',
      titulo: tr('Mis alertas', 'My alerts'),
      total: propias.length,
      verTodo: '/seguimiento',
      items: propias.map(a => ({
        clave: String(a.id),
        texto: a.titulo,
        detalle: a.vencida
          ? tr(`venció el ${fecha(a.fecha)}`, `due ${fecha(a.fecha)}`)
          : tr(`faltan ${a.diasRestantes} días`, `${a.diasRestantes} days left`),
        ayuda: a.codigos.join(', ') || tr('General', 'General'),
        enlace: a.codigos.length === 1 ? `/seguimiento/${encodeURIComponent(a.codigos[0]!)}` : '/seguimiento',
        urgente: a.vencida,
      })),
    },
    {
      clave: 'llegadas',
      titulo: tr('Llegadas y puerto', 'Arrivals and port'),
      total: llegadas.length + puerto.length,
      verTodo: '/seguimiento',
      items: [
        ...llegadas.map(a => ({
          clave: `l-${a.codigo}`,
          texto: a.codigo,
          detalle:
            a.tono === 'hoy'
              ? tr('llega hoy', 'arrives today')
              : tr(`ETA vencida hace ${Math.abs(a.dias)} días`, `ETA passed ${Math.abs(a.dias)} days ago`),
          enlace: `/seguimiento/${encodeURIComponent(a.codigo)}`,
          urgente: a.tono === 'vencida',
        })),
        ...puerto.map(p => ({
          clave: `p-${p.codigo}`,
          texto: p.codigo,
          detalle:
            p.diasConCobro > 0
              ? tr(`${p.diasConCobro} días con cobro en puerto`, `${p.diasConCobro} charged days in port`)
              : tr(`quedan ${p.diasLibresRestantes} días libres`, `${p.diasLibresRestantes} free days left`),
          enlace: `/seguimiento/${encodeURIComponent(p.codigo)}`,
          urgente: p.diasConCobro > 0,
        })),
      ],
    },
    {
      clave: 'calidad',
      titulo: tr('Calidad de datos', 'Data quality'),
      total: deCalidad.length,
      verTodo: '/calidad',
      items: deCalidad.map(a => ({
        clave: String(a.id),
        texto: a.titulo,
        detalle: tr(`${numero(a.casos)} casos`, `${numero(a.casos)} cases`),
        // Lleva directo a los casos de esa revision para corregirlos.
        enlace: `/calidad?revision=${encodeURIComponent(a.clave)}`,
        urgente: a.severidad === 'error',
      })),
    },
    {
      clave: 'cumplimiento',
      titulo: tr('Cumplimiento', 'Compliance'),
      total: cumplimiento.data?.avisos ?? 0,
      verTodo: '/cumplimiento',
      items: deCumplimiento.map(a => ({
        clave: `${a.vinculoId}-${a.tipoId}`,
        texto: `${a.tipo} · ${a.vinculo}`,
        detalle:
          a.diasParaVencer === null
            ? undefined
            : a.diasParaVencer < 0
              ? tr(`venció hace ${-a.diasParaVencer} días`, `expired ${-a.diasParaVencer} days ago`)
              : a.diasParaVencer === 0
                ? tr('vence hoy', 'expires today')
                : tr(`vence en ${a.diasParaVencer} días`, `expires in ${a.diasParaVencer} days`),
        ayuda: a.responsable ? tr(`Responsable: ${a.responsable}`, `Responsible: ${a.responsable}`) : undefined,
        enlace: '/cumplimiento',
        urgente: a.situacion === 'vencido',
      })),
    },
    {
      clave: 'navieras',
      titulo: tr('Navieras', 'Shipping lines'),
      total: cambiosDeNavieras,
      verTodo: '/seguimiento/navieras',
      items: cambiosDeNavieras
        ? [
            {
              clave: 'cambios',
              texto: tr(
                `${numero(cambiosDeNavieras)} cambios de ETD, ETA o buque por revisar`,
                `${numero(cambiosDeNavieras)} ETD, ETA or vessel changes to review`,
              ),
              enlace: '/seguimiento/navieras',
            },
          ]
        : [],
    },
  ];
  const conAlgo = grupos.filter(g => g.total > 0);
  const cargando =
    (veReservas && reservas.isPending) || (veSeguimiento && seguimiento.isPending) || (veCalidad && calidad.isPending);

  return (
    <section
      className="rounded-lg border border-linea bg-white px-4 py-3"
      aria-labelledby="titulo-pendientes"
      data-prueba="pendientes-hoy"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 id="titulo-pendientes" className="text-sm font-medium">
          {tr('Pendientes hoy', 'To do today')}
        </h2>
        {seguimiento.data && <span className="text-xs text-apagado">{fecha(seguimiento.data.hoy)}</span>}
        {cargando && !conAlgo.length ? (
          <span className="text-sm text-apagado">{tr('Revisando…', 'Checking…')}</span>
        ) : (
          !conAlgo.length && (
            <span className="text-sm text-apagado">{tr('Nada pendiente hoy. ✓', 'Nothing pending today. ✓')}</span>
          )
        )}
      </div>
      {conAlgo.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {conAlgo.map(g => (
            <div
              key={g.clave}
              className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
              data-prueba={`pendientes-${g.clave}`}
            >
              <h3 className="w-40 shrink-0 text-xs font-semibold tracking-wide text-apagado uppercase">
                {g.titulo} <span className="text-tinta">({numero(g.total)})</span>
                {AYUDA_PENDIENTES[g.clave] && <NotaDeAyuda titulo={g.titulo} texto={AYUDA_PENDIENTES[g.clave]!} />}
              </h3>
              <ul className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                {g.items.slice(0, MAXIMO).map(i => (
                  <li
                    key={i.clave}
                    className={`max-w-xs rounded-full border px-2.5 py-0.5 ${
                      i.resaltado
                        ? 'border-urgente bg-urgente-claro'
                        : i.urgente
                          ? 'border-alerta/40 bg-white'
                          : 'border-linea bg-white'
                    }`}
                    data-prueba={i.resaltado ? 'pendiente-resaltado' : undefined}
                    title={[i.ayuda, i.detalle].filter(Boolean).join(' · ') || undefined}
                  >
                    <Link to={i.enlace} className="flex min-w-0 items-baseline gap-1.5 hover:underline">
                      <span
                        aria-hidden
                        className={`text-xs ${i.resaltado ? 'text-urgente' : i.urgente ? 'text-alerta' : 'text-dorado'}`}
                      >
                        ●
                      </span>
                      <span className="truncate text-verde">{i.texto}</span>
                      {i.detalle && <span className="shrink-0 text-xs text-apagado">{i.detalle}</span>}
                    </Link>
                  </li>
                ))}
                {g.total > MAXIMO && (
                  <li className="self-center">
                    <Link to={g.verTodo} className="text-xs text-verde hover:underline">
                      {tr(`+${numero(g.total - MAXIMO)} · ver todo`, `+${numero(g.total - MAXIMO)} · see all`)}
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
